import Parse from 'parse';
import { InjectionToken, Provider } from '@angular/core';
import {
  ParseCaseRepository,
  ParseCaseTypeRepository,
  ParseAssetTypeRepository,
  ParseMethodTypeRepository,
  ParseResourceRepository,
  ParseDeviceTypeRepository,
  ParseDeviceRepository,
  ParseSampleRepository,
  ParsePipelineRepository,
  ParseLabelRepository,
  CountLabelsCloudQuery,
  ListLabelsCloudQuery,
  ParseWorkspaceRepository,
  ParseAlgorithmRepository,
} from '@telespot/infrastructure/parse';
import { LABEL_PAGINATION_MODE, LabelState } from '../state/label.state';
import { COUNT_LABELS_QUERY, LabelEffects, LIST_LABELS_QUERY, SAVE_LABEL_COMMAND } from '../effects/label.effects';
import { PipelineState } from '../state/pipeline.state';
import {
  COUNT_METHOD_TYPES_BY_PIPELINE_QUERY,
  COUNT_PIPELINES_QUERY,
  COUNT_STEPS_FOR_PIPELINE_QUERY,
  LIST_PIPELINES_QUERY,
  PipelineEffects,
} from '../effects/pipeline.effects';
import { PipelineStepDetailState } from '../state/pipeline-step-detail.state';
import {
  PipelineStepDetailEffects,
  RETRIEVE_PIPELINE_STEP_COMPATIBILITY_QUERY,
} from '../effects/pipeline-step-detail.effects';
import {
  COUNT_CASE_TYPES_BY_METHOD_TYPE_QUERY,
  COUNT_METHOD_TYPES_QUERY,
  COUNT_SAMPLES_BY_METHOD_TYPE_QUERY,
  DELETE_METHOD_TYPE_COMMAND,
  LIST_METHOD_TYPES_QUERY,
  MethodTypeListEffects,
} from '../effects/method-type-list.effects';
import { MethodTypeListState, METHOD_TYPE_LIST_PAGINATION_MODE } from '../state/method-type-list.state';
import {
  ANY_METHOD_TYPES_FOR_RESOURCE_QUERY,
  COUNT_RESOURCES_QUERY,
  DELETE_RESOURCE_COMMAND,
  LIST_RESOURCES_QUERY,
  ResourceEffects,
} from '../effects/resource.effects';
import { ResourceState } from '../state/resource.state';
import {
  ANY_DEVICES_FOR_DEVICE_TYPE_QUERY,
  COUNT_DEVICES_BY_DEVICE_TYPE_QUERY,
  COUNT_DEVICE_TYPES_QUERY,
  DeviceTypeEffects,
  LIST_DEVICE_TYPES_QUERY,
  RETRIEVE_DEVICE_TYPE_QUERY,
} from '../effects/device-type.effects';
import { DEVICE_TYPE_PAGINATION, DeviceTypeState } from '../state/device-type.state';
import {
  ANY_METHOD_TYPES_FOR_ASSET_TYPE_QUERY,
  AssetTypeEffects,
  COUNT_ASSET_TYPES_QUERY,
  COUNT_CASE_TYPES_BY_ASSET_TYPE_QUERY,
  COUNT_METHOD_TYPES_BY_ASSET_TYPE_QUERY,
  DELETE_ASSET_TYPE_COMMAND,
  LIST_ASSET_TYPES_QUERY,
  RETRIEVE_ASSET_TYPE_QUERY,
  SAVE_ASSET_TYPE_HANDLER,
} from '../effects/asset-type.effects';
import { ASSET_TYPE_STATE_CONFIG, AssetTypeState } from '../state/asset-type.state';
import {
  ANY_CASES_FOR_CASE_TYPE_QUERY,
  CaseTypeEffects,
  COUNT_CASES_BY_CASE_TYPE_QUERY,
  COUNT_CASE_TYPES_QUERY,
  COUNT_WORKSPACES_BY_CASE_TYPE_QUERY,
  DELETE_CASE_TYPE_COMMAND,
  LIST_CASE_TYPES_QUERY,
} from '../effects/case-type.effects';
import { CaseTypeState } from '../state/case-type.state';
import { PaginatedStateLoadingMode } from '@shared/ui';
import {
  AnyCasesForCaseTypeQuery,
  AnyDevicesForDeviceTypeQuery,
  AnyMethodTypesForAssetTypeQuery,
  AnyMethodTypesForResourceQuery,
  CountAlgorithmsQuery,
  CountAssetTypesQuery,
  CountCaseTypesByAssetTypeQuery,
  CountCaseTypesByMethodTypeQuery,
  CountCaseTypesQuery,
  CountCasesByCaseTypeQuery,
  CountDevicesByDeviceTypeQuery,
  CountDeviceTypesQuery,
  CountMethodTypesByAssetTypeQuery,
  CountMethodTypesByPipelineQuery,
  CountMethodTypesQuery,
  CountPipelinesQuery,
  CountResourcesQuery,
  CountSamplesByMethodTypeQuery,
  CountStepsForPipelineQuery,
  CountWorkspacesByCaseTypeQuery,
  DeleteAssetTypeCommand,
  DeleteCaseTypeCommand,
  DeleteMethodTypeCommand,
  DeleteResourceCommand,
  ListAlgorithmsQuery,
  ListAssetTypesQuery,
  ListCaseTypesQuery,
  ListDeviceTypesQuery,
  ListMethodTypesQuery,
  ListPipelinesQuery,
  ListResourcesQuery,
  RetrieveAlgorithmQuery,
  RetrieveAssetTypeQuery,
  RetrieveDeviceTypeQuery,
  RetrievePipelineStepCompatibilityQuery,
  SaveAssetTypeHandler,
  SaveLabelCommand,
  type ILabelNamesPort,
} from '@protocols/use-cases';
import { ParseLabelNamesRepository } from '@protocols/infra/parse';
import {
  AlgorithmRepository,
  type AssetTypeRepository,
  CaseRepository,
  CaseTypeRepository,
  DeviceRepository,
  DeviceTypeRepository,
  LabelRepository,
  MethodTypeRepository,
  PipelineRepository,
  ResourceRepository,
  SampleRepository,
  WorkspaceRepository,
} from '@telespot/domain';
import { ALGORITHM_PAGINATION_MODE, AlgorithmState } from '../state/algorithm.state';
import {
  AlgorithmEffects,
  COUNT_ALGORITHMS_QUERY,
  LIST_ALGORITHMS_QUERY,
  RETRIEVE_ALGORITHM_QUERY,
} from '../effects/algorithm.effects';
import { AlgorithmDetailState } from '../state/algorithm-detail.state';
import { AlgorithmDetailEffects } from '../effects/algorithm-detail.effects';

const LABEL_REPOSITORY = new InjectionToken<LabelRepository>('LABEL_REPOSITORY');

const LABEL_STATE_PROVIDERS: Provider[] = [
  {
    provide: LABEL_REPOSITORY,
    useFactory: () => new ParseLabelRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: COUNT_LABELS_QUERY,
    useFactory: () => new CountLabelsCloudQuery(),
  },
  {
    provide: LIST_LABELS_QUERY,
    useFactory: () => new ListLabelsCloudQuery(),
  },
  {
    provide: SAVE_LABEL_COMMAND,
    useFactory: (labelRepository: LabelRepository) => new SaveLabelCommand(labelRepository),
    deps: [LABEL_REPOSITORY],
  },
  LabelState,
  LabelEffects,
];

export function labelStoreProviders(pagination: PaginatedStateLoadingMode): Provider[] {
  return [
    {
      provide: LABEL_PAGINATION_MODE,
      useValue: pagination,
    },
    ...LABEL_STATE_PROVIDERS,
  ];
}

const PIPELINE_STORE_PIPELINE_REPOSITORY = new InjectionToken<PipelineRepository>('PIPELINE_STORE_PIPELINE_REPOSITORY');
const PIPELINE_STORE_METHOD_TYPE_REPOSITORY = new InjectionToken<MethodTypeRepository>(
  'PIPELINE_STORE_METHOD_TYPE_REPOSITORY'
);

export const pipelineStoreProviders: Provider[] = [
  {
    provide: PIPELINE_STORE_PIPELINE_REPOSITORY,
    useFactory: () => new ParsePipelineRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: PIPELINE_STORE_METHOD_TYPE_REPOSITORY,
    useFactory: () => new ParseMethodTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: COUNT_PIPELINES_QUERY,
    useFactory: (repo: PipelineRepository) => new CountPipelinesQuery(repo),
    deps: [PIPELINE_STORE_PIPELINE_REPOSITORY],
  },
  {
    provide: LIST_PIPELINES_QUERY,
    useFactory: (repo: PipelineRepository) => new ListPipelinesQuery(repo),
    deps: [PIPELINE_STORE_PIPELINE_REPOSITORY],
  },
  {
    provide: COUNT_STEPS_FOR_PIPELINE_QUERY,
    useFactory: (repo: PipelineRepository) => new CountStepsForPipelineQuery(repo),
    deps: [PIPELINE_STORE_PIPELINE_REPOSITORY],
  },
  {
    provide: COUNT_METHOD_TYPES_BY_PIPELINE_QUERY,
    useFactory: (repo: MethodTypeRepository) => new CountMethodTypesByPipelineQuery(repo),
    deps: [PIPELINE_STORE_METHOD_TYPE_REPOSITORY],
  },
  PipelineState,
  PipelineEffects,
];

const PIPELINE_STEP_DETAIL_PIPELINE_REPOSITORY = new InjectionToken<PipelineRepository>(
  'PIPELINE_STEP_DETAIL_PIPELINE_REPOSITORY'
);
const PIPELINE_STEP_DETAIL_LABEL_NAMES_PORT = new InjectionToken<ILabelNamesPort>(
  'PIPELINE_STEP_DETAIL_LABEL_NAMES_PORT'
);

export const pipelineStepDetailStoreProviders: Provider[] = [
  {
    provide: PIPELINE_STEP_DETAIL_PIPELINE_REPOSITORY,
    useFactory: () => new ParsePipelineRepository(Parse, { useMasterKey: false }),
  },
  // This tier's own label-name port instance, not a shared one — the two editor
  // tiers each build their own, and the adapter is stateless. Names come
  // through it rather than a label repository of this scope's own; wired
  // without the master key, because the browser has none.
  {
    provide: PIPELINE_STEP_DETAIL_LABEL_NAMES_PORT,
    useFactory: () => new ParseLabelNamesRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: RETRIEVE_PIPELINE_STEP_COMPATIBILITY_QUERY,
    useFactory: (repo: PipelineRepository, labelNamesPort: ILabelNamesPort) =>
      new RetrievePipelineStepCompatibilityQuery(repo, labelNamesPort),
    deps: [PIPELINE_STEP_DETAIL_PIPELINE_REPOSITORY, PIPELINE_STEP_DETAIL_LABEL_NAMES_PORT],
  },
  PipelineStepDetailState,
  PipelineStepDetailEffects,
];

const RESOURCE_STORE_RESOURCE_REPOSITORY = new InjectionToken<ResourceRepository>('RESOURCE_STORE_RESOURCE_REPOSITORY');
const RESOURCE_STORE_METHOD_TYPE_REPOSITORY = new InjectionToken<MethodTypeRepository>(
  'RESOURCE_STORE_METHOD_TYPE_REPOSITORY'
);

export const resourceStoreProviders: Provider[] = [
  {
    provide: RESOURCE_STORE_RESOURCE_REPOSITORY,
    useFactory: () => new ParseResourceRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: RESOURCE_STORE_METHOD_TYPE_REPOSITORY,
    useFactory: () => new ParseMethodTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: COUNT_RESOURCES_QUERY,
    useFactory: (repo: ResourceRepository) => new CountResourcesQuery(repo),
    deps: [RESOURCE_STORE_RESOURCE_REPOSITORY],
  },
  {
    provide: LIST_RESOURCES_QUERY,
    useFactory: (repo: ResourceRepository) => new ListResourcesQuery(repo),
    deps: [RESOURCE_STORE_RESOURCE_REPOSITORY],
  },
  {
    provide: DELETE_RESOURCE_COMMAND,
    useFactory: (repo: ResourceRepository) => new DeleteResourceCommand(repo),
    deps: [RESOURCE_STORE_RESOURCE_REPOSITORY],
  },
  {
    provide: ANY_METHOD_TYPES_FOR_RESOURCE_QUERY,
    useFactory: (repo: MethodTypeRepository) => new AnyMethodTypesForResourceQuery(repo),
    deps: [RESOURCE_STORE_METHOD_TYPE_REPOSITORY],
  },
  ResourceState,
  ResourceEffects,
];

const DEVICE_TYPE_STORE_DEVICE_TYPE_REPOSITORY = new InjectionToken<DeviceTypeRepository>(
  'DEVICE_TYPE_STORE_DEVICE_TYPE_REPOSITORY'
);
const DEVICE_TYPE_STORE_DEVICE_REPOSITORY = new InjectionToken<DeviceRepository>('DEVICE_TYPE_STORE_DEVICE_REPOSITORY');

const DEVICE_TYPE_STATE_PROVIDERS: Provider[] = [
  {
    provide: DEVICE_TYPE_STORE_DEVICE_TYPE_REPOSITORY,
    useFactory: () => new ParseDeviceTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: DEVICE_TYPE_STORE_DEVICE_REPOSITORY,
    useFactory: () => new ParseDeviceRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: COUNT_DEVICE_TYPES_QUERY,
    useFactory: (repo: DeviceTypeRepository) => new CountDeviceTypesQuery(repo),
    deps: [DEVICE_TYPE_STORE_DEVICE_TYPE_REPOSITORY],
  },
  {
    provide: LIST_DEVICE_TYPES_QUERY,
    useFactory: (repo: DeviceTypeRepository) => new ListDeviceTypesQuery(repo),
    deps: [DEVICE_TYPE_STORE_DEVICE_TYPE_REPOSITORY],
  },
  {
    provide: RETRIEVE_DEVICE_TYPE_QUERY,
    useFactory: (repo: DeviceTypeRepository) => new RetrieveDeviceTypeQuery(repo),
    deps: [DEVICE_TYPE_STORE_DEVICE_TYPE_REPOSITORY],
  },
  {
    provide: COUNT_DEVICES_BY_DEVICE_TYPE_QUERY,
    useFactory: (repo: DeviceRepository) => new CountDevicesByDeviceTypeQuery(repo),
    deps: [DEVICE_TYPE_STORE_DEVICE_REPOSITORY],
  },
  {
    provide: ANY_DEVICES_FOR_DEVICE_TYPE_QUERY,
    useFactory: (repo: DeviceRepository) => new AnyDevicesForDeviceTypeQuery(repo),
    deps: [DEVICE_TYPE_STORE_DEVICE_REPOSITORY],
  },
  DeviceTypeState,
  DeviceTypeEffects,
];

export function deviceTypeStoreProviders(pagination: PaginatedStateLoadingMode): Provider[] {
  return [
    {
      provide: DEVICE_TYPE_PAGINATION,
      useValue: pagination,
    },
    ...DEVICE_TYPE_STATE_PROVIDERS,
  ];
}

const METHOD_TYPE_STORE_METHOD_TYPE_REPOSITORY = new InjectionToken<MethodTypeRepository>(
  'METHOD_TYPE_STORE_METHOD_TYPE_REPOSITORY'
);
const METHOD_TYPE_STORE_CASE_TYPE_REPOSITORY = new InjectionToken<CaseTypeRepository>(
  'METHOD_TYPE_STORE_CASE_TYPE_REPOSITORY'
);
const METHOD_TYPE_STORE_SAMPLE_REPOSITORY = new InjectionToken<SampleRepository>('METHOD_TYPE_STORE_SAMPLE_REPOSITORY');
const METHOD_TYPE_STORE_ASSET_TYPE_REPOSITORY = new InjectionToken<AssetTypeRepository>(
  'METHOD_TYPE_STORE_ASSET_TYPE_REPOSITORY'
);

const METHOD_TYPE_LIST_STATE_PROVIDERS: Provider[] = [
  {
    provide: METHOD_TYPE_STORE_METHOD_TYPE_REPOSITORY,
    useFactory: () => new ParseMethodTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: METHOD_TYPE_STORE_CASE_TYPE_REPOSITORY,
    useFactory: () => new ParseCaseTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: METHOD_TYPE_STORE_SAMPLE_REPOSITORY,
    useFactory: () => new ParseSampleRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: METHOD_TYPE_STORE_ASSET_TYPE_REPOSITORY,
    useFactory: () => new ParseAssetTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: LIST_METHOD_TYPES_QUERY,
    useFactory: (methodTypeRepo: MethodTypeRepository, assetTypeRepo: AssetTypeRepository) =>
      new ListMethodTypesQuery(methodTypeRepo, assetTypeRepo),
    deps: [METHOD_TYPE_STORE_METHOD_TYPE_REPOSITORY, METHOD_TYPE_STORE_ASSET_TYPE_REPOSITORY],
  },
  {
    provide: COUNT_METHOD_TYPES_QUERY,
    useFactory: (repo: MethodTypeRepository) => new CountMethodTypesQuery(repo),
    deps: [METHOD_TYPE_STORE_METHOD_TYPE_REPOSITORY],
  },
  {
    provide: DELETE_METHOD_TYPE_COMMAND,
    useFactory: (repo: MethodTypeRepository) => new DeleteMethodTypeCommand(repo),
    deps: [METHOD_TYPE_STORE_METHOD_TYPE_REPOSITORY],
  },
  {
    provide: COUNT_CASE_TYPES_BY_METHOD_TYPE_QUERY,
    useFactory: (repo: CaseTypeRepository) => new CountCaseTypesByMethodTypeQuery(repo),
    deps: [METHOD_TYPE_STORE_CASE_TYPE_REPOSITORY],
  },
  {
    provide: COUNT_SAMPLES_BY_METHOD_TYPE_QUERY,
    useFactory: (repo: SampleRepository) => new CountSamplesByMethodTypeQuery(repo),
    deps: [METHOD_TYPE_STORE_SAMPLE_REPOSITORY],
  },
  MethodTypeListState,
  MethodTypeListEffects,
];

export function methodTypeListStoreProviders(pagination: PaginatedStateLoadingMode): Provider[] {
  return [
    {
      provide: METHOD_TYPE_LIST_PAGINATION_MODE,
      useValue: pagination,
    },
    ...METHOD_TYPE_LIST_STATE_PROVIDERS,
  ];
}

const ASSET_TYPE_STORE_ASSET_TYPE_REPOSITORY = new InjectionToken<AssetTypeRepository>(
  'ASSET_TYPE_STORE_ASSET_TYPE_REPOSITORY'
);
const ASSET_TYPE_STORE_METHOD_TYPE_REPOSITORY = new InjectionToken<MethodTypeRepository>(
  'ASSET_TYPE_STORE_METHOD_TYPE_REPOSITORY'
);
const ASSET_TYPE_STORE_CASE_TYPE_REPOSITORY = new InjectionToken<CaseTypeRepository>(
  'ASSET_TYPE_STORE_CASE_TYPE_REPOSITORY'
);

const ASSET_TYPE_STATE_PROVIDERS: Provider[] = [
  {
    provide: ASSET_TYPE_STORE_ASSET_TYPE_REPOSITORY,
    useFactory: () => new ParseAssetTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: ASSET_TYPE_STORE_METHOD_TYPE_REPOSITORY,
    useFactory: () => new ParseMethodTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: ASSET_TYPE_STORE_CASE_TYPE_REPOSITORY,
    useFactory: () => new ParseCaseTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: COUNT_ASSET_TYPES_QUERY,
    useFactory: (repo: AssetTypeRepository) => new CountAssetTypesQuery(repo),
    deps: [ASSET_TYPE_STORE_ASSET_TYPE_REPOSITORY],
  },
  {
    provide: LIST_ASSET_TYPES_QUERY,
    useFactory: (repo: AssetTypeRepository) => new ListAssetTypesQuery(repo),
    deps: [ASSET_TYPE_STORE_ASSET_TYPE_REPOSITORY],
  },
  {
    provide: DELETE_ASSET_TYPE_COMMAND,
    useFactory: (repo: AssetTypeRepository) => new DeleteAssetTypeCommand(repo),
    deps: [ASSET_TYPE_STORE_ASSET_TYPE_REPOSITORY],
  },
  {
    provide: RETRIEVE_ASSET_TYPE_QUERY,
    useFactory: (repo: AssetTypeRepository) => new RetrieveAssetTypeQuery(repo),
    deps: [ASSET_TYPE_STORE_ASSET_TYPE_REPOSITORY],
  },
  {
    provide: ANY_METHOD_TYPES_FOR_ASSET_TYPE_QUERY,
    useFactory: (repo: MethodTypeRepository) => new AnyMethodTypesForAssetTypeQuery(repo),
    deps: [ASSET_TYPE_STORE_METHOD_TYPE_REPOSITORY],
  },
  {
    provide: COUNT_METHOD_TYPES_BY_ASSET_TYPE_QUERY,
    useFactory: (repo: MethodTypeRepository) => new CountMethodTypesByAssetTypeQuery(repo),
    deps: [ASSET_TYPE_STORE_METHOD_TYPE_REPOSITORY],
  },
  {
    provide: COUNT_CASE_TYPES_BY_ASSET_TYPE_QUERY,
    useFactory: (repo: CaseTypeRepository) => new CountCaseTypesByAssetTypeQuery(repo),
    deps: [ASSET_TYPE_STORE_CASE_TYPE_REPOSITORY],
  },
  {
    provide: SAVE_ASSET_TYPE_HANDLER,
    useFactory: (assetTypeRepository: AssetTypeRepository) => new SaveAssetTypeHandler(assetTypeRepository),
    deps: [ASSET_TYPE_STORE_ASSET_TYPE_REPOSITORY],
  },
  AssetTypeState,
  AssetTypeEffects,
];

export function assetTypeStoreProviders(pagination: PaginatedStateLoadingMode): Provider[] {
  return [
    {
      provide: ASSET_TYPE_STATE_CONFIG,
      useValue: { loadingMode: pagination },
    },
    ...ASSET_TYPE_STATE_PROVIDERS,
  ];
}

const CASE_TYPE_STORE_CASE_TYPE_REPOSITORY = new InjectionToken<CaseTypeRepository>(
  'CASE_TYPE_STORE_CASE_TYPE_REPOSITORY'
);
const CASE_TYPE_STORE_CASE_REPOSITORY = new InjectionToken<CaseRepository>('CASE_TYPE_STORE_CASE_REPOSITORY');
const CASE_TYPE_STORE_WORKSPACE_REPOSITORY = new InjectionToken<WorkspaceRepository>(
  'CASE_TYPE_STORE_WORKSPACE_REPOSITORY'
);

export const caseTypeStoreProviders: Provider[] = [
  {
    provide: CASE_TYPE_STORE_CASE_TYPE_REPOSITORY,
    useFactory: () => new ParseCaseTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: CASE_TYPE_STORE_CASE_REPOSITORY,
    useFactory: () => new ParseCaseRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: CASE_TYPE_STORE_WORKSPACE_REPOSITORY,
    useFactory: () => new ParseWorkspaceRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: COUNT_CASE_TYPES_QUERY,
    useFactory: (repo: CaseTypeRepository) => new CountCaseTypesQuery(repo),
    deps: [CASE_TYPE_STORE_CASE_TYPE_REPOSITORY],
  },
  {
    provide: LIST_CASE_TYPES_QUERY,
    useFactory: (repo: CaseTypeRepository) => new ListCaseTypesQuery(repo),
    deps: [CASE_TYPE_STORE_CASE_TYPE_REPOSITORY],
  },
  {
    provide: DELETE_CASE_TYPE_COMMAND,
    useFactory: (repo: CaseTypeRepository) => new DeleteCaseTypeCommand(repo),
    deps: [CASE_TYPE_STORE_CASE_TYPE_REPOSITORY],
  },
  {
    provide: COUNT_CASES_BY_CASE_TYPE_QUERY,
    useFactory: (repo: CaseRepository) => new CountCasesByCaseTypeQuery(repo),
    deps: [CASE_TYPE_STORE_CASE_REPOSITORY],
  },
  {
    provide: COUNT_WORKSPACES_BY_CASE_TYPE_QUERY,
    useFactory: (repo: WorkspaceRepository) => new CountWorkspacesByCaseTypeQuery(repo),
    deps: [CASE_TYPE_STORE_WORKSPACE_REPOSITORY],
  },
  {
    provide: ANY_CASES_FOR_CASE_TYPE_QUERY,
    useFactory: (repo: CaseRepository) => new AnyCasesForCaseTypeQuery(repo),
    deps: [CASE_TYPE_STORE_CASE_REPOSITORY],
  },
  CaseTypeState,
  CaseTypeEffects,
];

const ALGORITHM_REPOSITORY = new InjectionToken<AlgorithmRepository>('ALGORITHM_REPOSITORY');

const ALGORITHM_STATE_PROVIDERS: Provider[] = [
  {
    provide: ALGORITHM_REPOSITORY,
    useFactory: () => new ParseAlgorithmRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: COUNT_ALGORITHMS_QUERY,
    useFactory: (repo: AlgorithmRepository) => new CountAlgorithmsQuery(repo),
    deps: [ALGORITHM_REPOSITORY],
  },
  {
    provide: LIST_ALGORITHMS_QUERY,
    useFactory: (repo: AlgorithmRepository) => new ListAlgorithmsQuery(repo),
    deps: [ALGORITHM_REPOSITORY],
  },
  {
    provide: RETRIEVE_ALGORITHM_QUERY,
    useFactory: (repo: AlgorithmRepository) => new RetrieveAlgorithmQuery(repo),
    deps: [ALGORITHM_REPOSITORY],
  },
  AlgorithmState,
  AlgorithmEffects,
];

export function algorithmStoreProviders(pagination: PaginatedStateLoadingMode): Provider[] {
  return [
    {
      provide: ALGORITHM_PAGINATION_MODE,
      useValue: pagination,
    },
    ...ALGORITHM_STATE_PROVIDERS,
  ];
}

export const algorithmDetailStoreProviders: Provider[] = [AlgorithmDetailState, AlgorithmDetailEffects];
